import { Box, GridLegacy as Grid, Typography, useTheme } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import CopyableTypography from "components/Typography/CopyableTypography";
import { JobLog, JobStatus } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { ExpandableCell } from "routes/Customer/Waste/Manifests/ExpandableCell";
import { prettyPrintDateTimeWithSeconds } from "util/dates";

import { STAFF_JOB_LOGS_TABLE_QUERY } from "./api";

export function JobLogTable({ jobId }: { jobId: string }) {
  const theme = useTheme();
  const logColumns: OmnisearchGridColDef<JobLog>[] = [
    {
      field: "status",
      headerName: "Status",
      width: 120,
      enumValues: Object.values(JobStatus),
      filterKeyType: "enumAutocomplete",
    },
    {
      field: "createdAt",
      headerName: "Logged At",
      width: 180,
      renderCell: ({ row }) => <span>{prettyPrintDateTimeWithSeconds(row.createdAt)}</span>,
    },
    {
      field: "errorMessage",
      headerName: "Message",
      width: 400,
      renderCell: ({ row }) => <CopyableTypography variant="body2" text={row.errorMessage} />,
    },
    {
      field: "stackTrace",
      headerName: "Stack Trace",
      sortable: false,
      filterable: false,
      topAligned: true,
      renderCell: ({ row }) => (
        <Box height={"100%"}>
          <ExpandableCell
            rowId={row.id}
            items={row.stackTrace?.split("\n") ?? []}
            collapsedLines={5}
            render={(lines: string[]) => {
              return (
                <Box
                  component="code"
                  display="flex"
                  sx={{ whiteSpace: "pre-wrap", textWrap: "nowrap" }}
                >
                  {lines.join("\n")}
                </Box>
              );
            }}
          />
        </Box>
      ),
    },
  ];

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" fontWeight="medium">
        Status Logs:
      </Typography>
      <Box marginTop={theme.spacing(1)}>
        <OmnisearchDataGrid
          persistenceKey="job-logs"
          autosizeOptions={{
            columns: ["stackTrace"],
            includeOutliers: true,
            expand: true,
          }}
          isURLDriven={false}
          initialSortModel={[{ field: "createdAt", sort: "desc" }]}
          skip={!jobId}
          columns={logColumns}
          dataQuery={STAFF_JOB_LOGS_TABLE_QUERY}
          defaultSearch={`jobId:${jobId}`}
          getItems={(data) => data.jobLogs.items}
          getCount={(data) => data.jobLogs.count}
          rowHeight={"auto"}
          withPadding={false}
          initialPageSize={5}
          showManageColumns={true}
        />
      </Box>
    </Grid>
  );
}
